import type { EvalVideo } from "@sitescene/types";

import { Trash01, UploadCloud02 } from "@untitledui/icons";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel } from "@/components/detail-panel";
import { StatusMessage } from "@/components/status-message";
import { Button } from "@/components/ui/buttons/button";
import { eventPath } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	useDeleteGoldVideo,
	useGoldVideos,
	useUploadGoldVideos,
} from "@/features/gold-videos/use-gold-videos";
import { formatWhen } from "@/pages/plausibility/format";
import { cx } from "@/utils/cx";

import { GoldVideoEditModal } from "./GoldVideoEditModal";

/**
 * The gold standard library: clips with hand-written ground truth, uploaded
 * here or added from an event. The same records as the internal tool's
 * Videos page, so its use cases and runs see every clip added here.
 */
export const GoldVideos = () => {
	const videos = useGoldVideos();
	const remove = useDeleteGoldVideo();
	const [editing, setEditing] = useState<EvalVideo | null>(null);
	const rows = videos.data ?? [];

	const columns: Column<EvalVideo>[] = [
		{
			header: "Title",
			key: "title",
			render: (row) => (
				<span className="block max-w-56 truncate font-medium" title={row.title}>
					{row.title || row.filename}
				</span>
			),
		},
		{
			header: "Ground truth",
			key: "description",
			render: (row) =>
				row.description ? (
					<span className="block max-w-72 truncate" title={row.description}>
						{row.description}
					</span>
				) : (
					<span className="text-tertiary italic">Not written yet</span>
				),
		},
		{
			header: "Length",
			key: "duration",
			render: (row) => formatDuration(row.durationSeconds),
		},
		{
			header: "Start time",
			key: "start",
			render: (row) => formatWhen(row.startTime),
		},
		{
			header: "Severity",
			key: "severity",
			render: (row) => row.severity ?? "—",
		},
		{
			header: "Source",
			key: "source",
			render: (row) => <SourceLink video={row} />,
		},
		{
			className: "text-right",
			header: "",
			key: "actions",
			render: (row) => (
				// Keeps the click from also opening the row
				<span onClick={(event) => event.stopPropagation()}>
					<Button
						aria-label={`Delete ${row.title}`}
						color="tertiary"
						iconLeading={Trash01}
						isDisabled={remove.isPending}
						onClick={() => {
							if (window.confirm(`Delete "${row.title || row.filename}"?`)) {
								remove.mutate(row.id);
							}
						}}
						size="sm"
					/>
				</span>
			),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<UploadPanel />

			<div>
				{remove.error && (
					<p className="mb-3 text-sm text-error-primary" role="alert">
						{remove.error.message}
					</p>
				)}
				<StatusMessage
					empty="No gold standard videos yet. Upload a clip above, or add one from an event."
					error={videos.error}
					isEmpty={!videos.isLoading && rows.length === 0}
					isLoading={videos.isLoading}
					loading="Loading videos…"
				/>
				{rows.length > 0 && (
					<DataTable
						columns={columns}
						onRowClick={setEditing}
						rowKey={(row) => row.id}
						rows={rows}
					/>
				)}
			</div>

			{editing && (
				<GoldVideoEditModal onClose={() => setEditing(null)} video={editing} />
			)}
		</div>
	);
};

/** Pick or drop clips, then upload them one after another. */
const UploadPanel = () => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [dragging, setDragging] = useState(false);
	const [progress, setProgress] = useState(0);
	const [done, setDone] = useState(0);
	const upload = useUploadGoldVideos({
		onFileDone: (index) => setDone(index + 1),
		onProgress: setProgress,
	});

	const addFiles = (list: FileList | null) => {
		const picked = [...(list ?? [])].filter((file) =>
			file.type.startsWith("video/"),
		);
		setFiles((current) => [...current, ...picked]);
	};

	const start = () => {
		setDone(0);
		upload.mutate(files, { onSuccess: () => setFiles([]) });
	};

	return (
		<DetailPanel title="Upload clips">
			<div
				className={cx(
					"flex flex-col items-center gap-2 rounded-lg border border-dashed border-secondary p-6 text-center text-sm text-secondary",
					dragging && "border-brand bg-brand-50",
				)}
				onDragLeave={() => setDragging(false)}
				onDragOver={(event) => {
					event.preventDefault();
					setDragging(true);
				}}
				onDrop={(event) => {
					event.preventDefault();
					setDragging(false);
					addFiles(event.dataTransfer.files);
				}}
			>
				<UploadCloud02
					aria-hidden="true"
					className="size-6 text-fg-quaternary"
				/>
				<p>Drop MP4 clips here, or</p>
				<Button
					color="secondary"
					isDisabled={upload.isPending}
					onClick={() => inputRef.current?.click()}
					size="sm"
				>
					Choose files
				</Button>
				<input
					accept="video/mp4"
					className="hidden"
					multiple
					onChange={(event) => {
						addFiles(event.target.files);
						event.target.value = "";
					}}
					ref={inputRef}
					type="file"
				/>
			</div>

			{files.length > 0 && (
				<ul className="mt-3 flex flex-col gap-1 text-sm">
					{files.map((file, index) => (
						<li
							className="flex items-center justify-between"
							key={`${file.name}-${index}`}
						>
							<span className="truncate">{file.name}</span>
							<span className="text-tertiary">
								{upload.isPending && index === done
									? `${progress}%`
									: index < done
										? "Uploaded"
										: `${(file.size / 1_000_000).toFixed(1)} MB`}
							</span>
						</li>
					))}
				</ul>
			)}

			{upload.error && (
				<p className="mt-3 text-sm text-error-primary" role="alert">
					{upload.error.message}
				</p>
			)}

			<div className="mt-3 flex items-center justify-between gap-3">
				<p className="text-xs text-tertiary">
					Write each clip's ground truth after it uploads: click its row.
				</p>
				<div className="flex gap-2">
					{files.length > 0 && !upload.isPending && (
						<Button color="secondary" onClick={() => setFiles([])} size="sm">
							Clear
						</Button>
					)}
					<Button
						isDisabled={files.length === 0 || upload.isPending}
						onClick={start}
						size="sm"
					>
						{upload.isPending
							? `Uploading ${done + 1} of ${files.length}…`
							: files.length
								? `Upload ${files.length} clip${files.length === 1 ? "" : "s"}`
								: "Upload"}
					</Button>
				</div>
			</div>
		</DetailPanel>
	);
};

/**
 * The camera a copied clip came from, linking back to its event in that
 * organization. Uploaded clips have no source.
 */
const SourceLink = ({ video }: { video: EvalVideo }) => {
	const navigate = useNavigate();
	const { organizations, selectOrg } = useOrgSelection();
	const { source } = video;
	if (!source) return <span className="text-tertiary">Upload</span>;

	const canOpen = organizations.some((org) => org.orgCode === source.orgCode);
	const label = `${source.sensor.name} · ${source.installation.name}`;
	if (!canOpen) return <span title={source.orgCode}>{label}</span>;

	return (
		<button
			className="text-left text-brand-secondary hover:underline"
			onClick={(event) => {
				event.stopPropagation();
				selectOrg(source.orgCode);
				void navigate(eventPath(source.event));
			}}
			title={`Open the event in ${source.orgCode}`}
			type="button"
		>
			{label}
		</button>
	);
};

const formatDuration = (seconds: number): string => {
	const total = Math.round(seconds);
	const minutes = Math.floor(total / 60);
	return minutes ? `${minutes}m ${total % 60}s` : `${total}s`;
};
